import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';

const slugifyHeading = (text) => String(text).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const textOf = (children) => React.Children.toArray(children).map((c) => (typeof c === 'string' ? c : c?.props?.children ? textOf(c.props.children) : '')).join('');

/** Extract H2 headings for a table of contents. */
export function extractHeadings(md) {
  return (md || '').split('\n').filter((l) => /^##\s+/.test(l)).map((l) => {
    const text = l.replace(/^##\s+/, '').replace(/[*_`]/g, '').trim();
    return { text, id: slugifyHeading(text) };
  });
}

const components = {
  h2: ({ children }) => { const t = textOf(children); return <h2 id={slugifyHeading(t)} className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight mt-10 mb-4 scroll-mt-28">{children}</h2>; },
  h3: ({ children }) => <h3 className="font-['Space_Grotesk'] text-xl font-semibold mt-8 mb-3">{children}</h3>,
  h4: ({ children }) => <h4 className="font-semibold mt-6 mb-2">{children}</h4>,
  p: ({ children }) => <p className="leading-relaxed text-[15.5px] sm:text-base text-foreground/90 mb-4">{children}</p>,
  a: ({ href = '', children }) => {
    const internal = href.startsWith('/') && !href.startsWith('//');
    if (internal) return <Link to={href} className="text-[hsl(var(--primary))] font-medium underline decoration-[hsl(var(--primary))]/40 underline-offset-2 hover:decoration-[hsl(var(--primary))]">{children}</Link>;
    return <a href={href} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] font-medium underline decoration-[hsl(var(--primary))]/40 underline-offset-2 hover:decoration-[hsl(var(--primary))]">{children}</a>;
  },
  ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-1.5 marker:text-[hsl(var(--primary))]">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-1.5 marker:text-[hsl(var(--primary))] marker:font-semibold">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed text-foreground/90">{children}</li>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-[hsl(var(--primary))] bg-[hsl(var(--brand-mint))]/40 rounded-r-lg px-4 py-3 my-5 italic text-foreground/90 [&>p]:mb-0">{children}</blockquote>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  hr: () => <hr className="my-8 border-border" />,
  table: ({ children }) => <div className="overflow-x-auto my-6 rounded-xl border border-border"><table className="w-full text-sm">{children}</table></div>,
  thead: ({ children }) => <thead className="bg-[hsl(var(--secondary))]">{children}</thead>,
  th: ({ children }) => <th className="text-left font-semibold px-3 py-2 border-b border-border">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 border-b border-border/60 align-top">{children}</td>,
  code: ({ inline, children }) => inline
    ? <code className="rounded bg-[hsl(var(--secondary))] px-1.5 py-0.5 text-[13px] font-mono">{children}</code>
    : <pre className="rounded-xl bg-[hsl(var(--secondary))] p-4 overflow-x-auto text-[13px] font-mono my-5"><code>{children}</code></pre>,
  img: ({ src, alt }) => <img src={src} alt={alt || ''} loading="lazy" className="rounded-xl my-6 w-full" />,
};

/** Renders trusted markdown (blog articles) with the site's typography. HTML is never rendered raw. */
export function BlogMarkdown({ content, className = '' }) {
  return (
    <div className={`blog-content ${className}`} data-testid="blog-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} skipHtml>{content || ''}</ReactMarkdown>
    </div>
  );
}
